import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';

const findEventById = (events, contentfulId) => {
  return events?.find((event) => event.externalId === contentfulId);
};

const getEventsByIds = (ids, events) => {
  if (!ids || !events) return [];
  return ids
    .map((item) => {
      if (item?.sys?.contentType?.sys?.id === 'lpAsset') {
        return {
          ...item,
          itemType: 'asset',
          _id: item?.sys?.id,
          content: item,
        };
      }

      const event = findEventById(events, item?.sys?.id);
      if (event) {
        return {
          ...event,
          itemType: 'event',
        };
      }

      return null;
    })
    .filter(Boolean);
};

const filterByExcludeSlug = (events, excludeSlug) =>
  excludeSlug ? events.filter((event) => event.slug !== excludeSlug) : events;

export const useFeaturedEvents = ({
  blueprint,
  events,
  excludeSlug = null,
  fillFeatured = false,
  featuredCount = 4,
}) => {
  const [featuredEvents, setFeaturedEvents] = useState(null);
  const [heroFeaturedEvents, setHeroFeaturedEvents] = useState(null);
  const [hasHeroFeatured, setHasHeroFeatured] = useState(false);

  useEffect(() => {
    if (!blueprint || !events) return;

    if (blueprint.heroEventIds) {
      const heroEvents = getEventsByIds(blueprint.heroEventIds, events);
      setHeroFeaturedEvents(heroEvents);
      setHasHeroFeatured(heroEvents.length > 0);
    } else {
      setHeroFeaturedEvents([]);
      setHasHeroFeatured(false);
    }

    if (!blueprint.featuredEventIds) return;

    const rawFeaturedEvents = getEventsByIds(
      blueprint.featuredEventIds,
      events
    );
    const filteredFeaturedEvents = filterByExcludeSlug(
      rawFeaturedEvents,
      excludeSlug
    );

    // If fillFeatured is true and we have fewer than the desired count,
    // fill the list with additional events.
    if (fillFeatured && filteredFeaturedEvents.length < featuredCount) {
      const slotsRemaining = featuredCount - filteredFeaturedEvents.length;

      // Exclude events already in the blueprint's featured list and ones matching the excludeSlug.
      const nonFeaturedEvents = events.filter(
        (event) =>
          (!excludeSlug || event.slug !== excludeSlug) &&
          !blueprint.featuredEventIds.some(
            (feat) => feat?.sys?.id === event.externalId
          )
      );

      // Sort additional events by their start date.
      const sortedNonFeaturedEvents = nonFeaturedEvents.sort((a, b) => {
        const dateA = DateTime.fromISO(a.schedules?.[0]?.startDate);
        const dateB = DateTime.fromISO(b.schedules?.[0]?.startDate);
        return dateA.toMillis() - dateB.toMillis();
      });

      const fillerEvents = sortedNonFeaturedEvents.slice(0, slotsRemaining);
      setFeaturedEvents([...filteredFeaturedEvents, ...fillerEvents]);
    } else {
      setFeaturedEvents(filteredFeaturedEvents);
    }
  }, [blueprint, events, excludeSlug, fillFeatured, featuredCount]);

  return { featuredEvents, heroFeaturedEvents, hasHeroFeatured };
};
